import { useStatistics } from '@/app/hooks/useStatistics'
import { ReportTransactionParams } from '@/app/services/reportService/reportTransactions'
import { PartnerType } from '@/app/utils/partners.enum'
import { endOfDay, startOfDay } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

export enum ItemsLabel {
  pendings = 'Pendentes',
  reproveds = 'Reprovadas',
  inProgress = 'Em andamento',
  commission = 'Saldo para saque',
  partnerCommission = 'Saldo do parceiro',
  refunded = 'Estornos',
}

export const toMoney = (v?: number) => (v ?? 0) / 100

interface UseStatisticsCardProps {
  interval?: number | false
  onSelectDates?: (dates: { startDate: string; endDate: string }) => void
  sellerId?: string | undefined
  reportTransactionParams?: ReportTransactionParams | undefined
}

export const useStatisticsCard = ({
  interval = 5 * 60 * 1000,
  onSelectDates,
  sellerId,
  reportTransactionParams,
}: UseStatisticsCardProps) => {
  const {
    data,
    isFetching,
    isLoading,
    params,
    setParams,
    availableAmount,
    adminAmount,
    availableisLoading,
  } = useStatistics({ interval })
  const [product, setProductState] = useState<PartnerType>(
    () =>
      (sessionStorage.getItem('dashboard:product') as PartnerType) ||
      PartnerType.FGTS,
  )

  useEffect(() => {
    const persisted = sessionStorage.getItem(
      'dashboard:product',
    ) as PartnerType | null
    if (persisted && persisted !== PartnerType.FGTS) {
      setParams((prev) => ({ ...prev, type: persisted }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [selectedDate, setSelectedDate] = useState<DateRange | undefined>({
    from: new Date(params.startDate),
    to: new Date(params.endDate),
  })

  const statistics = data?.statistics
  const teamStats = data?.teamStatisticas

  const amounts = useMemo(
    () => ({
      total: statistics?.completed ?? 0,
      commission: availableAmount ?? 0,
      admin: adminAmount > 0 ? adminAmount : 0,
      completed: statistics?.completed_value ?? 0,
      myTeam: teamStats?.completed_value,
      teams:
        teamStats && teamStats.completed_value > 0
          ? statistics!.completed_value - teamStats.completed_value
          : toMoney(statistics?.completed_value),
    }),
    [statistics, availableAmount, teamStats],
  )

  const items = useMemo(
    () => [
      {
        label: ItemsLabel.pendings,
        count: statistics?.action_required ?? 0,
        value: statistics?.action_required_value ?? 0,
      },
      {
        label: ItemsLabel.reproveds,
        count: statistics?.refused ?? 0,
        value: statistics?.refused_value ?? 0,
      },
      {
        label: ItemsLabel.inProgress,
        count: statistics?.pending ?? 0,
        value: statistics?.pending_value ?? 0,
      },
      {
        label: ItemsLabel.refunded,
        count: statistics?.refunded ?? 0,
        value: statistics?.refunded_value ?? 0,
      },
    ],
    [statistics],
  )

  useEffect(() => {
    if (reportTransactionParams) {
      setParams((prev) => ({
        startDate: reportTransactionParams.startDate ?? prev.startDate,
        endDate: reportTransactionParams.endDate ?? prev.endDate,
        sellerIds: reportTransactionParams.sellerIds,
        customerIds: reportTransactionParams.customerIds,
        teamIds: reportTransactionParams.teamIds,
        supervisorIds: reportTransactionParams.supervisorIds,
        managerIds: reportTransactionParams.managerIds,
        leaderIds: reportTransactionParams.leaderIds,
      }))
    }
  }, [reportTransactionParams, setParams])

  const handleDateChange = (range?: DateRange) => {
    setSelectedDate(range)

    if (range?.from && range.to) {
      const start = formatInTimeZone(
        startOfDay(range.from),
        'America/Sao_Paulo',
        "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
      )
      const end = formatInTimeZone(
        endOfDay(range.to),
        'America/Sao_Paulo',
        "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
      )

      setParams((p) => ({
        ...p,
        type: product,
        startDate: start,
        endDate: end,
        sellerIds: sellerId,
      }))

      onSelectDates?.({ startDate: start, endDate: end })
    }
  }

  const handleChangeProduct = (product: PartnerType) => {
    sessionStorage.setItem('dashboard:product', product)
    setProductState(product)
    setParams((prev) => ({ ...prev, type: product, sellerIds: sellerId }))
  }

  const teamsContractNumber =
    (statistics?.completed ?? 0) - (teamStats?.completed ?? 0)

  return {
    product,
    handleChangeProduct,
    isFetching,
    isLoading,
    data,
    selectedDate,
    handleDateChange,
    availableisLoading,
    teamsContractNumber,
    amounts,
    items,
  }
}
